import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Leaf, MessageCircle } from "lucide-react";
import { IMAGES, waLink } from "@/data/content";

const LINES = [
  { text: "Cuidamos do seu", accent: false },
  { text: "espaço verde,", accent: true },
  { text: "durante todo o ano.", accent: false },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-28 sm:pt-36">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-lime-200/40 blur-3xl"
      />
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-5 pb-16 sm:pb-24 lg:grid-cols-12 lg:gap-8 lg:px-10">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.25em] text-lime-700"
          >
            <Leaf size={14} /> Jardinagem · Manutenção · Limpeza de Terrenos
          </motion.p>

          <h1 className="mt-6 font-serif text-4xl leading-[1.12] tracking-tight text-ink sm:text-5xl lg:text-[4.1rem]">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block ${line.accent ? "italic text-lime-600" : ""}`}
                  initial={{ y: "115%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
                >
                  {line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-moss sm:text-lg"
          >
            Serviços profissionais de jardinagem, manutenção de condomínios, corte de relva,
            sebes e limpeza de terrenos em Arruda dos Vinhos, Alenquer, Vila Franca de Xira,
            Loures e região de Lisboa.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="hero-quote-button"
              href="#orcamento"
              className="group flex items-center gap-2 rounded-full bg-pine px-8 py-4 text-base font-semibold text-ivory shadow-[0_18px_40px_-14px_rgba(18,37,26,0.5)] transition-colors duration-200 hover:bg-pine-hover"
            >
              Pedir Orçamento Gratuito
              <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              data-testid="hero-whatsapp-button"
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 rounded-full bg-[#25D366] px-8 py-4 text-base font-semibold text-white shadow-md transition-colors duration-200 hover:bg-[#1EBE5A]"
            >
              <MessageCircle size={18} /> WhatsApp
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1.1 }}
            className="mt-8 text-sm text-moss/80"
          >
            Arruda dos Vinhos · Alenquer · Vila Franca de Xira · Loures · Lisboa
          </motion.p>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ clipPath: "inset(100% 0 0 0)" }}
            animate={{ clipPath: "inset(0% 0 0 0)" }}
            transition={{ duration: 1.1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative overflow-hidden rounded-t-[10rem] rounded-b-[2rem] shadow-[0_40px_80px_-30px_rgba(18,37,26,0.4)]"
          >
            <motion.img
              style={{ y: imgY, scale: 1.15 }}
              src={IMAGES.hero}
              alt="Jardim cuidado com relvado e zonas verdes mantido pela EcoJard"
              className="h-[26rem] w-full object-cover sm:h-[30rem]"
              loading="eager"
            />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.05 }}
            className="absolute -bottom-6 left-4 flex items-center gap-3 rounded-2xl border border-stone2 bg-ivory/95 px-5 py-4 shadow-lg backdrop-blur sm:left-8"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-lime-100 text-lime-700">
              <Leaf size={18} />
            </span>
            <div>
              <p className="text-sm font-semibold text-ink">Orçamento gratuito</p>
              <p className="text-xs text-moss">Sem compromisso, com visita se necessário</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
